import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  MenuItem,
  Stack,
  Switch,
  TextField,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { addTitle, editTitle, getTitleById } from '../../../api/endpoints/corporate';
import type { Title } from '../../../api/types/corporate';
import { ApiErrorAlert } from '../../../components/ApiErrorAlert';
import { useFreshEditRow } from '../../../components/useFreshEditRow';

const schema = z.object({
  name: z.string().min(1, 'Unvan adı gerekli'),
  level: z.coerce.number().min(0),
  canViewSubordinates: z.boolean(),
  parentTitleId: z.coerce.number().optional(),
  isActive: z.boolean(),
});

type FormValues = z.infer<typeof schema>;

const emptyValues: FormValues = {
  name: '',
  level: 0,
  canViewSubordinates: false,
  parentTitleId: undefined,
  isActive: true,
};

function toFormValues(t: Title): FormValues {
  return {
    name: t.name ?? '',
    level: t.level ?? 0,
    canViewSubordinates: t.canViewSubordinates ?? false,
    parentTitleId: t.parentTitleId ?? undefined,
    isActive: t.isActive ?? true,
  };
}

/** Create/edit dialog for Unvan. `options` is the flattened, depth-indented title list used
 *  to populate the "Üst Unvan" select — `excludeIds` (self + descendants when editing) keeps
 *  the picker from letting a title become its own ancestor. `canViewSubordinates` controls
 *  whether an employee holding this title can see subordinates' bookings/approvals. */
export function TitleFormDialog({
  open,
  title,
  options,
  excludeIds,
  onClose,
}: {
  open: boolean;
  /** null = create mode; otherwise edit mode for this row. */
  title: Title | null;
  options: { id: number; label: string }[];
  excludeIds: Set<number>;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const isEdit = !!title;

  const {
    control,
    handleSubmit,
    register,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: emptyValues,
  });

  // Refetch the fresh row on edit-open; silently falls back to the list row on error.
  const seed = useFreshEditRow({
    open,
    editing: title,
    queryKey: ['corporate', 'title', title?.id],
    fetch: async (row) => (await getTitleById(row.id)).titles?.[0],
  });

  useEffect(() => {
    if (open) reset(seed ? toFormValues(seed) : emptyValues);
  }, [open, seed, reset]);

  const mutation = useMutation({
    mutationFn: (values: FormValues) =>
      isEdit && title
        ? editTitle({ ...values, id: title.id, parentTitleId: values.parentTitleId ?? null })
        : addTitle({ ...values, parentTitleId: values.parentTitleId ?? null }),
    onSuccess: () => {
      enqueueSnackbar(isEdit ? 'Unvan güncellendi.' : 'Unvan oluşturuldu.', { variant: 'success' });
      queryClient.invalidateQueries({ queryKey: ['corporate', 'titles'] });
      onClose();
    },
    onError: () => {
      /* surfaced via ApiErrorAlert below */
    },
  });

  const submit = handleSubmit((values) => mutation.mutate(values));
  const selectableOptions = options.filter((o) => !excludeIds.has(o.id));

  return (
    <Dialog open={open} onClose={() => !mutation.isPending && onClose()} maxWidth="sm" fullWidth>
      <DialogTitle>{isEdit ? 'Unvanı Güncelle' : 'Yeni Unvan'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 8 }}>
              <TextField
                fullWidth
                size="small"
                label="Unvan Adı"
                error={!!errors.name}
                helperText={errors.name?.message}
                {...register('name')}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Seviye"
                error={!!errors.level}
                helperText={errors.level?.message}
                {...register('level')}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Controller
                control={control}
                name="parentTitleId"
                render={({ field }) => (
                  <TextField {...field} value={field.value ?? ''} select fullWidth size="small" label="Üst Unvan">
                    <MenuItem value="">Kök (üst unvan yok)</MenuItem>
                    {selectableOptions.map((o) => (
                      <MenuItem key={o.id} value={o.id}>
                        {o.label}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Controller
                control={control}
                name="canViewSubordinates"
                render={({ field }) => (
                  <FormControlLabel
                    control={<Switch checked={!!field.value} onChange={(e) => field.onChange(e.target.checked)} />}
                    label="Astların Kayıtlarını Görebilir"
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Controller
                control={control}
                name="isActive"
                render={({ field }) => (
                  <FormControlLabel
                    control={<Switch checked={!!field.value} onChange={(e) => field.onChange(e.target.checked)} />}
                    label="Aktif"
                  />
                )}
              />
            </Grid>
          </Grid>
          <ApiErrorAlert error={mutation.error} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={mutation.isPending}>
          Vazgeç
        </Button>
        <Button onClick={submit} variant="contained" disabled={mutation.isPending}>
          {mutation.isPending ? 'Kaydediliyor…' : 'Kaydet'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
